import React, { useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import LmsLookupDefinitionsTable from './components/LmsLookupDefinitionsTable';
import LmsLookupDefinitionForm from './components/LmsLookupDefinitionForm';
import useLmsLookupDefinitionsData from './hooks/useLmsLookupDefinitionsData';
import GenericTableFilter from '../../../components/common/GenericTableFilter';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useLmsPermission } from '../hooks/useLmsPermission';
import { LMS } from '../../../constants/permissionCodes';

/**
 * Eğitim Tipi ve Eğitim Lokasyonu tanım ekranları için ortak sayfa gövdesi.
 * Ekranlar arasındaki tek fark `groupCode` (TRAINING_TYPES / TRAINING_LOCATION) ve başlık.
 */
const LmsLookupDefinitionsPage = ({ groupCode, pageTitleKey, allowDelete = true, showCodeField = false, showSortField = false, showActiveField = false }) => {
    const { th } = useLanguage();
    const { can } = useLmsPermission();
    const [selectedItem, setSelectedItem] = useState(null);
    const [dialogVisible, setDialogVisible] = useState(false);
    // GenericTableFilter kendi iç `filters` state'ini yalnızca mount'ta `initialFilters`'tan
    // besliyor; reset'te key değiştirip remount ettirmezsek iki tıklama gerekiyor
    // (bkz. [[project_lms_module]] Pass 9, training-catalog/index.jsx aynı düzeltme).
    const [filterResetKey, setFilterResetKey] = useState(0);
    const toast = useRef(null);

    const { lookupApi, isLoading, lazyParams, onPage, saveDefinition, activeFilters, handleFilterChange, initFilters, deleteDefinition, refetch } = useLmsLookupDefinitionsData(groupCode);

    const handleEdit = (rowData) => {
        setSelectedItem(rowData);
        setDialogVisible(true);
    };

    const handleCreateClick = () => {
        setSelectedItem(null);
        setDialogVisible(true);
    };

    const handleHide = () => {
        setDialogVisible(false);
        setSelectedItem(null);
    };

    const handleSubmit = async (formData) => {
        const success = await saveDefinition(formData);
        if (success) {
            handleHide();
            refetch?.();
        }
    };

    // Silme işlevi — bazı ekranlarda (ör. Eğitim Tipi Tanımları) `allowDelete={false}` ile
    // geçici olarak kapatılıyor: fonksiyon burada duruyor (Eğitim Lokasyonu hâlâ kullanıyor)
    // ama render'da yalnızca `allowDelete` true ise Form'a geçiriliyor.
    const handleDelete = (id) => {
        if (!id) return;
        confirmDialog({
            message: th('lmsLookupDefinitions.confirmDeleteMessage'),
            header: th('common.delete'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await deleteDefinition(id);
                    toast.current?.show({ severity: 'success', summary: th('common.success'), detail: th('lmsLookupDefinitions.deleteSuccess'), life: 3000 });
                    handleHide();
                    refetch?.();
                } catch {
                    toast.current?.show({ severity: 'error', summary: th('common.error'), detail: th('lmsLookupDefinitions.deleteError'), life: 4000 });
                }
            }
        });
    };

    const filterFields = useMemo(
        () => [
            { field: 'code', headerName: th('lmsLookupDefinitions.filterCode'), type: 'text' },
            { field: 'text', headerName: th('lmsLookupDefinitions.filterText'), type: 'text' },
            ...(showActiveField
                ? [
                      {
                          field: 'isActive',
                          headerName: th('common.status'),
                          type: 'dropdown',
                          options: [
                              { value: 1, label: th('common.active', { fallback: 'Aktif' }) },
                              { value: 0, label: th('common.inactive', { fallback: 'Pasif' }) }
                          ],
                          optionLabel: 'label',
                          optionValue: 'value'
                      }
                  ]
                : [])
        ],
        [th, showActiveField]
    );

    const onApplyFilters = (uiFilters) => {
        handleFilterChange(uiFilters);
    };

    const onResetFilters = () => {
        initFilters();
        setFilterResetKey((prev) => prev + 1);
    };

    return (
        <div className="card">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th(pageTitleKey)}</h1>
                {can(LMS.DEFINITION_CREATE) && <Button label={th('lmsLookupDefinitions.newButton')} icon="pi pi-plus" onClick={handleCreateClick} />}
            </div>

            <div className="mb-2">
                <GenericTableFilter
                    key={`${groupCode}-filter-${filterResetKey}`}
                    filterFields={filterFields}
                    initialFilters={{ code: activeFilters?.code ?? '', text: activeFilters?.text ?? '', isActive: activeFilters?.isActive ?? null }}
                    onSearch={() => {}}
                    onApplyFilters={onApplyFilters}
                    onResetFilters={onResetFilters}
                />
            </div>

            <LmsLookupDefinitionsTable data={lookupApi} totalCount={lookupApi?.totalCount} loading={isLoading} lazyParams={lazyParams} onPage={onPage} onEdit={handleEdit} showActiveField={showActiveField} />

            {dialogVisible && (
                <LmsLookupDefinitionForm
                    visible={dialogVisible}
                    onHide={handleHide}
                    onSubmit={handleSubmit}
                    initialData={selectedItem}
                    loading={isLoading}
                    handleDelete={allowDelete && can(LMS.DEFINITION_DELETE) ? handleDelete : undefined}
                    readOnly={!!selectedItem && !can(LMS.DEFINITION_UPDATE)}
                    showCodeField={showCodeField}
                    showSortField={showSortField}
                    showActiveField={showActiveField}
                />
            )}
        </div>
    );
};

export default LmsLookupDefinitionsPage;
